export interface SanitizationFinding {
  path: string;
  kind: 'ipv4' | 'ipv6' | 'mac' | 'serial' | 'ssid' | 'token_like';
  value: string;
}

export interface SanitizationResult {
  cleaned: string;
  findings: SanitizationFinding[];
}

const IPV4 =
  /\b(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b/g;
const IPV6 =
  /\b(?:[0-9a-fA-F]{1,4}:){2,7}[0-9a-fA-F]{1,4}\b/g;
const MAC =
  /\b(?:[0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}\b/g;
const SERIAL = /\b(?:SN|Serial)\s*[:#-]?\s*[A-Za-z0-9/-]{4,}\b/gi;
const SSID = /\bSSID\s*[=:]\s*["']?[^\s"'<>]{1,32}/gi;
/** Long opaque tokens, but not UUID / monitorKey-like codes */
const TOKEN_LIKE =
  /\b(?:Bearer\s+)?(?![0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b)[A-Za-z0-9_\-]{32,}\b/g;

const SKIP_KEYS = new Set([
  'id',
  'siteId',
  'zoneId',
  'sourceNodeId',
  'targetNodeId',
  'monitorKey',
  'monitorMode',
  'monitorSource',
  'connectionType',
  'type',
  'criticality',
  'expectedPresence',
  'schemaVersion',
  'color',
  'portCount',
  'portLayout',
  'portProfileId',
  'wifiAntennaCount',
  'interfaces',
  'sourcePort',
  'targetPort',
  'labelT',
  'routeOffset',
  'networkCidr',
  // Local inventory (browser-only; not cloud telemetry)
  'macLocal',
  'ipLocal',
  'model',
  'vendor',
  'location',
  'photoDataUrl',
  'appearanceId',
  'lifecycle',
]);

function collect(kind: SanitizationFinding['kind'], re: RegExp, text: string, path: string) {
  const findings: SanitizationFinding[] = [];
  const matches = text.match(re) ?? [];
  for (const value of matches) {
    findings.push({ path, kind, value });
  }
  return findings;
}

/** Remove network identifiers from free-text before cloud save. */
export function sanitizeText(text: string, path = 'text'): SanitizationResult {
  const findings = [
    ...collect('ipv4', IPV4, text, path),
    ...collect('ipv6', IPV6, text, path),
    ...collect('mac', MAC, text, path),
    ...collect('serial', SERIAL, text, path),
    ...collect('ssid', SSID, text, path),
    ...collect('token_like', TOKEN_LIKE, text, path),
  ];

  let cleaned = text;
  cleaned = cleaned.replace(IPV4, '[REDACTED_IPV4]');
  cleaned = cleaned.replace(IPV6, '[REDACTED_IPV6]');
  cleaned = cleaned.replace(MAC, '[REDACTED_MAC]');
  cleaned = cleaned.replace(SERIAL, '[REDACTED_SERIAL]');
  cleaned = cleaned.replace(SSID, 'SSID=[REDACTED]');
  cleaned = cleaned.replace(TOKEN_LIKE, '[REDACTED_TOKEN]');

  return { cleaned, findings };
}

export function sanitizeDiagramStrings<T>(doc: T): {
  document: T;
  findings: SanitizationFinding[];
} {
  const findings: SanitizationFinding[] = [];
  const clone = structuredClone(doc);

  const walk = (value: unknown, path: string, key?: string): unknown => {
    if (typeof value === 'string') {
      if (key && SKIP_KEYS.has(key)) return value;
      const result = sanitizeText(value, path);
      findings.push(...result.findings);
      return result.cleaned;
    }
    if (Array.isArray(value)) {
      return value.map((item, idx) => walk(item, `${path}[${idx}]`));
    }
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = walk(v, `${path}.${k}`, k);
      }
      return out;
    }
    return value;
  };

  return { document: walk(clone, '$') as T, findings };
}
